/**
 * Wire shapes for the guided-workflow draft resource (PLAT-4318).
 *
 * **Rewritten 2026-09-01 for the backend's draft-entity pivot**
 * (`specs/PLAT-4318-portal-workflow-redesign/design/draft-entity-pivot.md`).
 *
 * A draft is no longer a `DRAFT` status value on a `DataSource`/`Connector`/`Pipeline` row.
 * It is a row in its own `Draft` table, discriminated by `entity_type`, holding the
 * in-progress form data in a schema-agnostic `payload` JSON field. There is exactly **one**
 * endpoint family — `/api/drafts/` — not three per-entity sub-actions, which is why these
 * shapes live in `core/models` and are consumed through the single shared `DraftService`.
 */

/**
 * Which entity a draft will become at finalize.
 *
 * SCREAMING_SNAKE, matching the backend's `Draft.entity_type` choices. It travels as a
 * **request/query field**, never as a URL segment — the superseded `DraftEntityPath`
 * (`'connectors' | 'pipelines' | 'data-sources'`) is retired precisely because there is no
 * longer a per-entity URL for it to name.
 */
export type DraftEntityType = 'DATA_SOURCE' | 'CONNECTOR' | 'PIPELINE';

/**
 * The in-progress form tree.
 *
 * Schema-agnostic by design: `Draft.payload` is a `JSONField` with no model-layer validation,
 * so nothing rejects an incomplete payload at save time. Full validation is deferred entirely
 * to `finalize`, which runs the real entity's own create serializer. The per-flow "earliest
 * Save draft" step is therefore a **UX** choice, not a database constraint.
 */
export type DraftPayload = Record<string, unknown>;

/** A `Draft` row as the server returns it, from any of list / create / read / update. */
export interface Draft {
  readonly id: number;
  readonly entity_type: DraftEntityType;
  readonly payload: DraftPayload;
  readonly created_by?: number;
  /** The author's email, for the Created By column (PLAT-4481). `null` if none was recorded. */
  readonly created_by_email?: string | null;
  /**
   * Whether the caller may resume (change), finalize or delete this draft (PLAT-4482): every
   * user in the organisation sees every draft, but only its author or the CDO may change it.
   */
  readonly can_modify?: boolean;
  readonly created_at?: string;
  readonly updated_at?: string;
}

/** `POST /api/drafts/` request body. */
export interface DraftCreateRequest {
  readonly entity_type: DraftEntityType;
  readonly payload: DraftPayload;
}

/**
 * `PATCH /api/drafts/{id}/` request body.
 *
 * `entity_type` is fixed at creation and never re-sent — a draft does not change what it is
 * going to become.
 */
export interface DraftUpdateRequest {
  readonly payload: DraftPayload;
}

/** `GET /api/drafts/` — DRF's standard paginated envelope (`page_size=10`, `max_page_size=50`). */
export interface DraftListResponse {
  readonly count: number;
  readonly next: string | null;
  readonly previous: string | null;
  readonly results: Draft[];
}

/** Platform-standard field-level errors, as returned by a failed finalize. */
export interface DraftFieldErrors {
  readonly [field: string]: string[];
}

/**
 * What the server substitutes for a `protected_field` value when a draft is read back.
 *
 * `DraftSerializer.to_representation` runs `mask_draft_payload_secrets`, which is
 * `hide_secrets(..., to_representation=True)` — the same masking the real entity's
 * configuration read uses. The literal is fixed in `customer_portal/utils/json_schema_helper.py`
 * (`data[key] = "*****" if data[key] else ""`), verified against `portal-back` @ `3ec0d7c1`.
 */
export const DRAFT_MASKED_SECRET = '*****';

/**
 * Strip masked secrets out of a draft `configuration` sub-object before sending it back.
 *
 * **Why this is needed.** A draft PATCH replaces `payload` wholesale, and
 * `DraftSerializer.update` re-encrypts every `protected_field` key it finds. So echoing a
 * read-back `'*****'` on a re-save encrypts the *mask* over the real secret, and the eventual
 * finalize hands the new DataSource a credential that cannot work — silently, with no error at
 * any step. Dropping the key is how the client says "the user did not touch this one".
 *
 * **This is one half of a pair, and it stays necessary** (PLAT-4391). Omitting the key is what
 * tells the server the user did not retype that secret; `DraftSerializer.update` now reads the
 * omission and merges the stored ciphertext back in, marking the key `unchanged` so it is not
 * encrypted a second time — the same `remove_validation_from_unaffected_secrets` chain the real
 * entity's configuration update has always used. Before that merge existed, the omission meant
 * the stored password was dropped on every re-save and the next resume showed an empty field.
 *
 * So do NOT "simplify" this away by sending the mask back: that encrypts `'*****'` over a good
 * credential, silently, and the source fails at connect time with no clue why.
 */
export function stripMaskedSecrets(configuration: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(configuration).filter(([, value]) => value !== DRAFT_MASKED_SECRET));
}

/**
 * Drop the keys a half-filled form has nothing to say about.
 *
 * **`Draft.payload` IS the finalize request** — `DraftViewSet.finalize` passes the stored blob
 * straight to the real entity's own create serializer with no merge step. So a draft payload has
 * to be *create-shaped*, not a convenient subset: whatever is missing here is missing from the
 * entity that gets created. That is why each flow builds its draft payload from the same
 * assembly its terminal create uses, and prunes rather than hand-picking fields.
 *
 * Pruning matters because the create assembly emits placeholders for controls the user has not
 * reached yet — `dateShift(null)` returns `''`, an unset schedule becomes `['']` — and those
 * would fail `DateTimeField` validation at finalize where an absent key simply reads as "not
 * provided". An empty array prunes for the same reason a `null` does: it carries no information,
 * and a PATCH replaces `payload` wholesale, so omitting it is how a cleared selection is stored.
 */
export function prunePartialPayload(payload: Record<string, unknown>): DraftPayload {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => {
      if (value === null || value === undefined || value === '') {
        return false;
      }
      return !(Array.isArray(value) && value.filter((entry) => entry !== null && entry !== '').length === 0);
    }),
  );
}

/**
 * The label a merged draft row shows in its status column.
 *
 * **Client-side, not a wire value.** Under the superseded design the server put a `Draft` token
 * in the entity's own status field, so the chip rendered whatever arrived. A `Draft` row has no
 * such field now, so the three list pages synthesize this one string — kept here rather than
 * repeated per page so the chip, the colour map key and any filter all agree.
 */
export const DRAFT_ROW_LABEL = 'Draft';

/**
 * A draft rendered as a row on one of the three list pages.
 *
 * The list pages merge two responses — the entity's own list and `DraftService.list(type)` —
 * into one array. `isDraftRow` is a **client-synthesized** marker, not a server field: it
 * replaces the retired pattern of testing `connect_status === 'Draft'` / `status === 'DRAFT'`
 * against a token the server can no longer produce, because a `Draft` row is never a row in
 * the entity's own table.
 */
export interface DraftListRow {
  /** The `Draft`'s own id — Resume navigates to `?draft=<id>`, never an entity id. */
  readonly id: number;
  readonly isDraftRow: true;
  readonly entity_type: DraftEntityType;
  /** Best-effort from `payload`; frequently absent pre-finalize. */
  readonly name: string;
  /** The label the status column renders. Client-side, not a wire value. */
  readonly status: string;
  readonly payload: DraftPayload;
  readonly updated_at?: string;
  /** The author's email, for the Created By column (PLAT-4481); blank when unknown. */
  readonly created_by: string;
  /** Whether Resume and Delete are offered on this row (PLAT-4482) — see `canModifyDraft`. */
  readonly canModify: boolean;
  /** Columns the entity's own rows carry and a draft cannot — rendered blank, never `undefined`. */
  readonly [column: string]: unknown;
}

/**
 * Whether the caller may change this draft (PLAT-4482). The server says so per draft; a response
 * without the flag (an older server) is read as yes, which is what every draft meant before —
 * the server still refuses a change it does not allow.
 */
export function canModifyDraft(draft: Pick<Draft, 'can_modify'>): boolean {
  return draft.can_modify !== false;
}
